// LineChart — the dashboard line-chart look (hollow circular markers,
// legend with per-series totals under the plot), hand-rolled SVG like the
// Work Allocations Resolution Graph / sister-fork Finance custom. `points` is one
// entry per filtered item, { d: dueISO|'', v: { seriesKey: rand } }; the
// chart buckets them itself by the selected period (Day / Week / Month /
// Quarter — pills under the x-axis, Month default) on a CONTINUOUS axis
// from the earliest to the latest bucket, with a leading 'None' bucket
// for undated items. `cumulative` turns each line into a running total.
// A series def is { key, label, color } (color = plain hex). The TODAY
// marker sits proportionally inside its bucket. `legendLimit` collapses a
// long legend (the per-supplier chart) behind "Show more".
//
// `bars` (optional) overlays COLUMN series on their own right-hand y-axis:
// an array of { key, label, posColor, negColor, opacity, info }. The bar
// axis is symmetric about zero, so a positive value is a column standing
// UP from the zero line (posColor) and a negative one a column hanging
// DOWN (negColor); several bar series share a bucket side by side. Bars
// bucket and cumulate exactly like the lines and sit behind them. `info`
// (any series) is the text behind the ⓘ in the legend — how the figure
// is calculated — opened by a click. Every legend entry carries a
// CHECKBOX (on by default) that shows / hides that series — unless the
// parent passes `hidden` (a Set of series keys): then visibility is the
// parent's (a shared selection across charts) and the legend simply lists
// the VISIBLE series with their totals.
//
// `trend` (optional) = { keys: Set of series keys, fitTo: 'last'|'this',
// pct: outlier % } draws a TREND LINE for each key (lines and column
// series alike): a least-squares fit over the bucketed values up to the
// last complete period (or the current one), solid across the fitted
// periods, a dotted straight-line projection after them, a shaded 95%
// confidence band, and an × on every excluded outlier period. The fit's
// reliability and optimal outlier % are reported back through
// `onTrendStats({ key: { reliability, optimalPct } })`. A trend line stays
// drawn even when its series is unticked (the axes still make room for
// it, and the legend lists it as "<series> trend").

import { useEffect, useMemo, useRef, useState } from 'react';
import InfoIcon from './InfoIcon';
import { BUCKETS, FONT, NONE, bucketLabel, bucketStartOf, fmtR, nextBucketStart, niceMax, parseYmd, todayISO } from './util';
import { confidenceHalfWidth, optimalOutlierPct, trendSegments } from './trend';

// `fmt` / `fmtAxis` / `xLabel` / `yLabel` (optional) let a sister custom
// chart something other than rand by due date (hours by created date, say);
// the defaults are this page's rand look.
// `onPick({ start, bucket, keys, whole })` (optional) makes the chart a
// door (the Tempo custom-chart convention): click ONE line point or ONE
// column → only that series' items for that period (`keys` = [series]);
// click the period's x-axis label → the whole interval (`whole` = true,
// `keys` = every series showing). Hovering a point or column highlights
// it; a click anywhere else in a period's band is the whole interval too.
// `height` (optional) overrides the plot height; a Y-AXIS pill switches
// the line axis between Linear and Log (log lifts the small series off the
// floor when one spike dominates — columns keep their own linear axis).
export default function LineChart({ title, points, seriesDefs, bars, cumulative, ui, narrow, legendLimit = 0, emptyText = 'No finance data in the current filters.', hidden = null, trend = null, onTrendStats = null, fmt = fmtR, fmtAxis = null, xLabel = 'DUE DATE', yLabel = 'ZAR VALUE (EXCL VAT)', onPick = null, height = null }) {
  const [open, setOpen] = useState(true);
  const [bucket, setBucket] = useState('month');
  const [yScale, setYScale] = useState('lin');        // 'lin' | 'log' — the line axis
  const [off, setOff] = useState(() => new Set());      // hidden series keys (own mode)
  const [hover, setHover] = useState(null);
  const [legendAll, setLegendAll] = useState(false);
  const controlled = hidden instanceof Set;
  const isOn = (k) => (controlled ? !hidden.has(k) : !off.has(k));
  const toggle = (k) => setOff((o) => {
    const n = new Set(o);
    if (n.has(k)) n.delete(k); else n.add(k);
    return seriesDefs.some((s) => !n.has(s.key)) ? n : o;   // never hide the last line
  });
  const barDefs = useMemo(() => (Array.isArray(bars) ? bars : bars ? [bars] : []), [bars]);
  const allKeys = useMemo(() => [...seriesDefs.map((s) => s.key), ...barDefs.map((b) => b.key)], [seriesDefs, barDefs]);

  const buckets = useMemo(() => {
    let min = null, max = null, hasNone = false;
    points.forEach((p) => {
      if (!p.d) { hasNone = true; return; }
      const s = bucketStartOf(p.d, bucket);
      if (!min || s < min) min = s;
      if (!max || s > max) max = s;
    });
    const zero = () => Object.fromEntries(allKeys.map((k) => [k, 0]));
    const out = []; const map = new Map();
    const push = (start) => { const b = { start, v: zero() }; out.push(b); map.set(start, b); };
    if (hasNone) push(NONE);
    if (min) for (let s = min; s <= max; s = nextBucketStart(s, bucket)) { push(s); if (out.length > 2000) break; }
    points.forEach((p) => {
      const b = map.get(p.d ? bucketStartOf(p.d, bucket) : NONE);
      if (!b) return;
      allKeys.forEach((k) => { b.v[k] += p.v[k] || 0; });
    });
    return out;
  }, [points, allKeys, bucket]);

  const series = (def) => {
    let run = 0;
    const pts = buckets.map((b) => { run += b.v[def.key]; return cumulative ? run : b.v[def.key]; });
    const total = buckets.reduce((sum, b) => sum + b.v[def.key], 0);
    return { ...def, pts, total };
  };
  const rows = useMemo(() => seriesDefs.map(series), [seriesDefs, buckets, cumulative]); // eslint-disable-line react-hooks/exhaustive-deps
  const barRows = useMemo(() => barDefs.map(series), [barDefs, buckets, cumulative]); // eslint-disable-line react-hooks/exhaustive-deps
  const trendOn = (k) => !!(trend && trend.keys && trend.keys.has(k));
  const active = rows.filter((r) => isOn(r.key));
  const activeBars = barRows.filter((r) => isOn(r.key));
  const barsOn = activeBars.length > 0 || barRows.some((r) => trendOn(r.key));
  // Axes scale to the visible series PLUS any hidden series whose trend
  // line is on, so a trend never runs off the plot.
  const scaleRows = rows.filter((r) => isOn(r.key) || trendOn(r.key));
  const scaleBars = barRows.filter((r) => isOn(r.key) || trendOn(r.key));
  const maxY = useMemo(() => niceMax(Math.max(1, ...scaleRows.flatMap((r) => r.pts))), [scaleRows]); // eslint-disable-line react-hooks/exhaustive-deps
  // The bar axis spans the columns' actual negative and positive extents
  // (nice-rounded), and BOTH axes share ONE zero line: the line axis gets
  // the same below-zero share of the plot as the bar axis needs, so R 0
  // sits at the same height left and right.
  const barExt = useMemo(() => {
    const vals = scaleBars.flatMap((r) => r.pts);
    const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
    return { min: lo < 0 ? -niceMax(-lo) : 0, max: hi > 0 ? niceMax(hi) : (lo < 0 ? 0 : 1) };
  }, [scaleBars]); // eslint-disable-line react-hooks/exhaustive-deps
  const barsOnAxis = barsOn && (barExt.max > 0 || barExt.min < 0);
  const isLog = yScale === 'log';
  const isDarkUi = String(ui.card || '').toLowerCase() === '#1f1f23';
  const zeroFrac = barsOnAxis && !isLog ? (-barExt.min) / (barExt.max - barExt.min) : 0;   // share of the plot below zero
  const minY = isLog ? 0 : zeroFrac >= 0.95 ? -maxY : -maxY * zeroFrac / (1 - zeroFrac);
  const maxB = barExt.max, minB = barExt.min;
  // Log floor: a decade under the smallest positive value on show (never below R 1).
  const logFloor = useMemo(() => {
    const pos = scaleRows.flatMap((r) => r.pts).filter((v) => v > 0);
    const mn = pos.length ? Math.min(...pos) : 1;
    return Math.max(1, 10 ** Math.floor(Math.log10(mn)));
  }, [scaleRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const n = buckets.length;
  const W = 1100; const H = height || (narrow ? 300 : 380);
  const ticks = 5;
  // Compact axis labels (R 60M · R 12.5M · R 800k) sized to the margins.
  const compact = (v) => {
    const a = Math.abs(v); const sign = v < 0 ? '-' : '';
    const num = a >= 1e9 ? `${+(a / 1e9).toFixed(2)}bn` : a >= 1e6 ? `${+(a / 1e6).toFixed(a >= 1e8 ? 0 : 1)}M` : a >= 1e3 ? `${+(a / 1e3).toFixed(a >= 1e5 ? 0 : 1)}k` : `${Math.round(a)}`;
    return `${sign}R ${num}`;
  };
  const axisFmtDefault = fmtAxis || compact;
  // Round tick steps that always pass through zero, for both axes.
  const tickVals = (lo, hi) => {
    const span = Math.max(1e-9, hi - lo);
    const step = niceMax(span) / ticks;
    const out = [];
    for (let k = Math.ceil(lo / step - 1e-9); k * step <= hi + 1e-9; k += 1) out.push(k * step);
    if (!out.includes(0) && lo <= 0 && hi >= 0) out.push(0);
    return out.sort((a, b) => a - b);
  };
  const logTicks = () => { const out = []; for (let e = Math.log10(logFloor); 10 ** e <= maxY * 1.0001; e += 1) out.push(10 ** e); if (!out.length || out[out.length - 1] < maxY) out.push(maxY); return out; };
  const leftTicks = isLog ? logTicks() : tickVals(minY, maxY);
  const rightTicks = barsOnAxis ? tickVals(minB, maxB) : [];
  const charW = 6.6;
  const leftW = Math.max(...leftTicks.map((v) => axisFmtDefault(v).length), 4) * charW;
  const rightW = rightTicks.length ? Math.max(...rightTicks.map((v) => axisFmtDefault(v).length), 4) * charW : 0;
  const M = { l: 30 + leftW, r: barDefs.length ? 30 + Math.max(rightW, 40) : 24, t: 18, b: 78 };
  const plotW = W - M.l - M.r; const plotH = H - M.t - M.b;
  const bandW = n ? plotW / n : plotW;
  const xC = (i) => M.l + bandW * (i + 0.5);
  const yLin = (v) => M.t + plotH * (1 - (v - minY) / (maxY - minY));
  const yLog = (v) => { const lo = Math.log10(logFloor); const hi = Math.log10(Math.max(maxY, logFloor * 10)); const x = Math.log10(Math.max(v, logFloor)); return M.t + plotH * (1 - (x - lo) / (hi - lo)); };
  const yC = isLog ? yLog : yLin;
  const yB = (v) => (barsOnAxis ? M.t + plotH * (1 - (v - minB) / (maxB - minB)) : yC(0));
  const zeroY = isLog ? (barsOnAxis ? M.t + plotH * (1 - (0 - minB) / (maxB - minB)) : M.t + plotH) : yC(0);
  const labelEvery = n <= 24 ? 1 : Math.ceil(n / 20);
  const showMarkers = n <= 120;                        // per-day over years = no dots
  const markerR = n <= 30 ? 5 : n <= 60 ? 3.6 : 2.6;
  const barW = Math.max(2, (bandW * 0.62) / Math.max(1, activeBars.length));
  const barX = (i, j) => xC(i) - (barW * activeBars.length) / 2 + barW * j;
  const todayIso = todayISO();
  const todayX = useMemo(() => {
    if (!n) return null;
    const bs = bucketStartOf(todayIso, bucket);
    const i = buckets.findIndex((b) => b.start === bs);
    if (i < 0) return null;
    const span = Math.max(1, (parseYmd(nextBucketStart(bs, bucket)) - parseYmd(bs)) / 86400000);
    const dayIn = (parseYmd(todayIso) - parseYmd(bs)) / 86400000;
    return M.l + bandW * (i + (dayIn + 0.5) / span);
  }, [n, buckets, todayIso, bucket, bandW, M.l]);
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    if (n < 1) return;
    setHover(Math.max(0, Math.min(n - 1, Math.floor((px - M.l) / bandW))));
  };
  const hv = hover != null ? hover : null;
  const [hit, setHit] = useState(null);                 // { key, i } — the point / column under the mouse
  const pick = (e, keys, i, whole) => {
    if (!onPick) return;
    e.stopPropagation();
    onPick({ start: buckets[i].start, bucket, keys, whole: !!whole });
  };
  const allVisibleKeys = () => [...active.map((r) => r.key), ...activeBars.map((r) => r.key)];

  // ---- trend lines --------------------------------------------------------
  // x = bucket index. Fit over the buckets from the first non-zero value up
  // to the last COMPLETE bucket ('last') or including the current one
  // ('this'); the 'None' bucket never takes part. Column series fit in
  // their own (bar) axis space.
  const allRows = useMemo(() => [...rows.map((r) => ({ ...r, kind: 'line' })), ...barRows.map((r) => ({ ...r, kind: 'bar' }))], [rows, barRows]);
  const trendLines = useMemo(() => {
    if (!trend || !trend.keys || !trend.keys.size || n < 2) return [];
    const curStart = bucketStartOf(todayIso, bucket);
    const inFit = (b) => b.start !== NONE && (trend.fitTo === 'this' ? b.start <= curStart : b.start < curStart);
    const lastX = n - 1;
    const out = [];
    allRows.forEach((r) => {
      if (!trend.keys.has(r.key)) return;                 // hidden series keep their trend line
      const all = buckets.map((b, i) => ({ x: i, y: r.pts[i], ok: inFit(b) }));
      const firstNZ = all.findIndex((q) => q.ok && q.y !== 0);
      if (firstNZ < 0) return;
      const pts = all.slice(firstNZ).filter((q) => q.ok).map((q) => ({ x: q.x, y: q.y }));
      const seg = trendSegments({ points: pts, pct: trend.pct || 0, lastX });
      if (!seg) return;
      const yOf = r.kind === 'bar' ? yB : yC;
      const clamp = r.kind === 'bar' ? (v) => Math.max(minB, Math.min(maxB, v)) : (v) => Math.max(minY, Math.min(maxY, v));
      const px = (q) => `${xC(q.x).toFixed(1)},${yOf(clamp(q.y)).toFixed(1)}`;
      let band = null;
      if ((seg.fit.usedPoints || 0) >= 3) {
        const up = [], lo = [];
        for (let x = seg.x0; x <= lastX + 1e-6; x += 0.25) {
          const h = confidenceHalfWidth(seg.fit, x);
          up.push({ x, y: seg.central(x) + h }); lo.push({ x, y: seg.central(x) - h });
        }
        band = [...up.map(px), ...lo.reverse().map(px)].join(' ');
      }
      const color = r.color || r.posColor;
      out.push({ key: r.key, label: r.label, color, seg, band, px,
        sloped: seg.sloped.map(px).join(' '), projected: seg.projected ? seg.projected.map(px).join(' ') : null,
        excluded: (seg.excluded || []).map((q) => ({ x: xC(q.x), y: yOf(clamp(q.y)) })),
        reliability: seg.reliability, optimalPct: optimalOutlierPct(pts, lastX) });
    });
    return out;
  }, [trend, allRows, buckets, n, bucket, todayIso, maxY, minY, maxB, minB, off, hidden]); // eslint-disable-line react-hooks/exhaustive-deps
  const trendStatsKey = trendLines.map((t) => `${t.key}:${t.reliability == null ? 'n' : t.reliability.toFixed(4)}:${t.optimalPct}`).join('|');
  useEffect(() => {
    if (!onTrendStats) return;
    onTrendStats(Object.fromEntries(trendLines.map((t) => [t.key, { reliability: t.reliability, optimalPct: t.optimalPct, label: t.label }])));
  }, [trendStatsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const trendOf = (key) => trendLines.find((t) => t.key === key);

  const svgRef = useRef(null);
  // Export = the chart plus a TITLE above it and a LEGEND (visible series
  // with totals) below it, on a padded card — not the bare plot.
  const exportPng = () => {
    const svg = svgRef.current; if (!svg) return;
    const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const PAD = 24; const TITLE_H = 44; const LINE_H = 24; const CHAR_W = 7.2;
    const items = [
      ...rows.filter((r) => isOn(r.key)).map((r) => ({ label: r.label, total: fmt(r.total), sw: (x, y) => `<circle cx="${x + 7}" cy="${y}" r="7" fill="${r.color}"/>` })),
      ...barRows.filter((r) => isOn(r.key)).map((r) => ({ label: r.label, total: fmt(r.total), sw: (x, y) => `<rect x="${x}" y="${y - 7}" width="6" height="14" rx="2" fill="${r.posColor}"/><rect x="${x + 8}" y="${y - 7}" width="6" height="14" rx="2" fill="${r.negColor}"/>` })),
    ];
    // lay the legend out in rows that fit the chart width
    const lines = [[]]; let used = 0;
    items.forEach((it) => {
      const w = 24 + (it.label.length + it.total.length + 3) * CHAR_W + 28;
      if (used + w > W - 2 * PAD && lines[lines.length - 1].length) { lines.push([]); used = 0; }
      lines[lines.length - 1].push({ ...it, w }); used += w;
    });
    const LEG_H = lines.length * LINE_H + 12;
    const TW = W + 2 * PAD; const TH = PAD + TITLE_H + H + LEG_H + PAD;
    let legend = '';
    lines.forEach((ln, li) => {
      const total = ln.reduce((a, it) => a + it.w, 0);
      let x = PAD + (W - total) / 2; const y = PAD + TITLE_H + H + 14 + li * LINE_H;
      ln.forEach((it) => {
        legend += it.sw(x, y);
        legend += `<text x="${x + 22}" y="${y + 4.5}" font-family="${esc(FONT)}" font-size="13" font-weight="700" fill="${ui.fg}">${esc(it.label)}<tspan fill="${ui.muted}"> | </tspan><tspan fill="${ui.blue}">${esc(it.total)}</tspan></text>`;
        x += it.w;
      });
    });
    const inner = svg.innerHTML;
    const xml = `<svg xmlns="http://www.w3.org/2000/svg" width="${TW}" height="${TH}" viewBox="0 0 ${TW} ${TH}">`
      + `<rect width="${TW}" height="${TH}" fill="${ui.card}"/>`
      + `<text x="${TW / 2}" y="${PAD + 24}" text-anchor="middle" font-family="${esc(FONT)}" font-size="20" font-weight="800" fill="${ui.fg}">${esc(`${title} — ${bucketName.toUpperCase()}`)}</text>`
      + `<text x="${TW / 2}" y="${PAD + 40}" text-anchor="middle" font-family="${esc(FONT)}" font-size="11" fill="${ui.muted}">${esc(`Exported ${todayISO()}`)}</text>`
      + `<g transform="translate(${PAD} ${PAD + TITLE_H})">${inner}</g>`
      + legend + `</svg>`;
    const url = URL.createObjectURL(new Blob([xml], { type: 'image/svg+xml;charset=utf-8' }));
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = TW * scale; canvas.height = TH * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = ui.card; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((png) => {
        if (!png) return;
        const a = document.createElement('a');
        a.href = URL.createObjectURL(png);
        a.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${bucket}-${todayISO()}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }, 'image/png');
    };
    img.src = url;
  };

  const tiny = { fontSize: 12, color: ui.muted, fontFamily: FONT };
  const pill = (on) => ({ fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${on ? ui.primary : ui.border}`, background: on ? `${ui.primary}14` : ui.card, color: on ? ui.fg : ui.muted, fontFamily: FONT });
  const bucketName = BUCKETS.find((b) => b.key === bucket)?.label || 'Per Month';
  const legendRows = legendLimit && !legendAll ? rows.slice(0, legendLimit) : rows;
  const axisFmt = axisFmtDefault;
  const barColor = (b, v) => (v < 0 ? b.negColor : b.posColor);
  // Legend entry: [✓] swatch Label | total  ⓘ — the checkbox (and the
  // label) toggles the series; the ⓘ opens the formula bubble.
  const legendItem = (r, swatch) => {
    const on = isOn(r.key);
    if (controlled) {
      if (!on) return null;                              // the shared selection hides it
      return (
        <span key={r.key} style={{ ...tiny, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '3px 8px', borderRadius: 8, border: `1px solid ${ui.border}`, background: ui.card }}>
          {swatch}
          <span style={{ fontWeight: 700, color: ui.fg }}>{r.label}</span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ color: ui.blue, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{hv != null ? fmt(r.pts[hv]) : fmt(r.total)}</span>
          {r.info && <InfoIcon text={r.info} title={`${r.label} — how it is calculated`} ui={ui} size={15} />}
          {trendOf(r.key) && (
            <span title={`Trend line — reliability ${trendOf(r.key).reliability == null ? 'n/a (fewer than 3 fitted periods)' : `${(trendOf(r.key).reliability * 100).toFixed(1)}%`}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginLeft: 2, paddingLeft: 7, borderLeft: `1px solid ${ui.border}` }}>
              <span style={{ width: 16, height: 0, borderTop: `3px solid ${r.color || r.posColor}`, display: 'inline-block' }} />
              <span style={{ fontSize: 11, fontWeight: 700 }}>trend</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: ui.fg, fontVariantNumeric: 'tabular-nums' }}>{trendOf(r.key).reliability == null ? 'n/a' : `${(trendOf(r.key).reliability * 100).toFixed(1)}%`}</span>
            </span>
          )}
        </span>
      );
    }
    return (
      <label key={r.key} title={on ? 'Untick to hide this series' : 'Tick to show this series'}
        style={{ ...tiny, display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '3px 8px', borderRadius: 8, border: `1px solid ${on ? ui.border : 'transparent'}`, background: on ? ui.card : 'transparent', opacity: on ? 1 : 0.55 }}>
        <input type="checkbox" checked={on} onChange={() => toggle(r.key)} style={{ width: 15, height: 15, margin: 0, accentColor: r.color || r.posColor || ui.blue, cursor: 'pointer' }} />
        {swatch}
        <span style={{ fontWeight: 700, color: ui.fg, textDecoration: on ? 'none' : 'line-through' }}>{r.label}</span>
        <span style={{ opacity: 0.6 }}>|</span>
        <span style={{ color: ui.blue, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {hv != null && on ? fmt(r.pts[hv]) : fmt(r.total)}
        </span>
        {r.info && <InfoIcon text={r.info} title={`${r.label} — how it is calculated`} ui={ui} size={15} />}
      </label>
    );
  };

  return (
    <div style={{ background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 12, boxShadow: ui.shadowSm, padding: '12px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flex: 1, minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT, color: ui.fg }}>
          <span style={{ fontSize: 12, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s', display: 'inline-block' }}>▸</span>
          <span style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title} — {bucketName.toUpperCase()}</span>
        </button>
        {open && <button onClick={exportPng} title="Download the chart as a PNG image" style={pill(false)}>Export PNG</button>}
      </div>
      {open && (n === 0 ? (
        <div style={{ ...tiny, padding: '18px 0', textAlign: 'center' }}>{emptyText}</div>
      ) : (<>
        <svg ref={svgRef} xmlns="http://www.w3.org/2000/svg" width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', width: '100%', maxWidth: '100%', height: 'auto', margin: '6px auto 0', background: ui.card, cursor: onPick && hv != null ? 'pointer' : 'default' }} onMouseMove={onMove} onMouseLeave={() => { setHover(null); setHit(null); }}
          onClick={(e) => { if (onPick && hv != null && buckets[hv]) pick(e, allVisibleKeys(), hv, true); }}>
          {onPick && <title>Click a point or a column for that series only; click anywhere else in a period, or its label, for the whole interval</title>}
          {leftTicks.map((v) => (
            <g key={`t${v}`}>
              <line x1={M.l} x2={W - M.r} y1={yC(v)} y2={yC(v)} stroke={ui.border} strokeWidth="1" />
              <text x={M.l - 8} y={yC(v) + 4.5} textAnchor="end" fontSize="12" fontWeight="600" fill={ui.fg} fontFamily={FONT}>{axisFmt(v)}</text>
            </g>
          ))}
          {/* the bar axis on the right: its own round ticks, zero on the SAME line as the left axis */}
          {barsOnAxis && rightTicks.map((v) => (
            <text key={`b${v}`} x={W - M.r + 8} y={yB(v) + 4.5} textAnchor="start" fontSize="11" fontWeight="600" fill={ui.fg} fontFamily={FONT}>{axisFmt(v)}</text>
          ))}
          {hv != null && <rect x={M.l + bandW * hv} y={M.t} width={bandW} height={plotH} fill={`${ui.primary}0c`} />}
          {/* columns first, so the lines draw over them */}
          {activeBars.map((b, j) => b.pts.map((v, i) => (v === 0 ? null : (
            <rect key={`bar${b.key}${i}`} x={barX(i, j)} y={Math.min(zeroY, yB(v))} width={barW} height={Math.max(1, Math.abs(yB(v) - zeroY))} fill={barColor(b, v)}
              opacity={hit && hit.key === b.key && hit.i === i ? Math.min(1, (b.opacity ?? 0.32) + 0.35) : (b.opacity ?? 0.32)} stroke={barColor(b, v)} strokeWidth={hit && hit.key === b.key && hit.i === i ? 2 : 1}
              style={{ cursor: onPick ? 'pointer' : 'default' }} onMouseEnter={() => setHit({ key: b.key, i })} onMouseLeave={() => setHit(null)} onClick={(e) => pick(e, [b.key], i)}>
              {onPick && <title>{`${b.label} · ${bucketLabel(buckets[i].start, bucket)} · ${fmt(v)} — click to open only these work items`}</title>}
            </rect>
          ))))}

          {active.map((r) => (
            <path key={r.key} d={r.pts.map((v, i) => `${i ? 'L' : 'M'}${xC(i).toFixed(1)},${yC(v).toFixed(1)}`).join(' ')} fill="none" stroke={r.color} strokeWidth="2.5" strokeLinejoin="round" />
          ))}
          {/* trend confidence bands sit behind everything */}
          {trendLines.map((t) => t.band && <polygon key={`band-${t.key}`} points={t.band} fill={t.color} opacity="0.12" />)}
          {showMarkers && active.map((r) => r.pts.map((v, i) => {
            const on = hit && hit.key === r.key && hit.i === i;
            return (
              <g key={`${r.key}-${i}`} style={{ cursor: onPick ? 'pointer' : 'default' }} onMouseEnter={() => setHit({ key: r.key, i })} onMouseLeave={() => setHit(null)} onClick={(e) => pick(e, [r.key], i)}>
                {onPick && <title>{`${r.label} · ${bucketLabel(buckets[i].start, bucket)} · ${fmt(v)} — click to open only these work items`}</title>}
                <circle cx={xC(i)} cy={yC(v)} r={Math.max(9, markerR + 5)} fill="transparent" />
                <circle cx={xC(i)} cy={yC(v)} r={on ? markerR + 2 : markerR} fill={on ? r.color : ui.card} stroke={r.color} strokeWidth="2" />
              </g>
            );
          }))}
          {/* R 0 — one solid black line right through the plot, on top of the columns and the lines
              (the shared zero in Linear; in Log the line axis has no zero, so it marks the columns' zero,
              or the plot floor when no columns are showing) */}
          <line x1={M.l} x2={W - M.r} y1={zeroY} y2={zeroY} stroke={isDarkUi ? '#f5f5f7' : '#000000'} strokeWidth="2" strokeLinecap="square" />
          {/* trend lines: solid over the fitted periods, dotted projection after, × on excluded outliers */}
          {trendLines.map((t) => (
            <g key={`trend-${t.key}`} pointerEvents="none">
              <polyline points={t.sloped} fill="none" stroke={ui.card} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points={t.sloped} fill="none" stroke={t.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
              {t.projected && <polyline points={t.projected} fill="none" stroke={ui.card} strokeWidth="6" strokeLinecap="round" />}
              {t.projected && <polyline points={t.projected} fill="none" stroke={t.color} strokeWidth="3" strokeDasharray="2 7" strokeLinecap="round" />}
              {t.excluded.map((e, i) => (
                <g key={`x${i}`} stroke={t.color} strokeWidth="2.2" strokeLinecap="round">
                  <line x1={e.x - 5} y1={e.y - 5} x2={e.x + 5} y2={e.y + 5} /><line x1={e.x - 5} y1={e.y + 5} x2={e.x + 5} y2={e.y - 5} />
                </g>
              ))}
            </g>
          ))}
          {todayX != null && (
            <g>
              <line x1={todayX} x2={todayX} y1={M.t} y2={M.t + plotH} stroke={ui.fg} strokeWidth="1.5" strokeDasharray="5 4" opacity="0.65" />
              <text x={todayX} y={M.t - 5} textAnchor="middle" fontSize="10.5" fontWeight="800" fill={ui.fg} fontFamily={FONT}>TODAY</text>
            </g>
          )}
          {buckets.map((b, i) => (i % labelEvery ? null : (
            <g key={`x${b.start}`} transform={`translate(${xC(i)} ${M.t + plotH + 8})`} style={{ cursor: onPick ? 'pointer' : 'default' }} onClick={(e) => pick(e, allVisibleKeys(), i, true)}>
              {onPick && <title>{`${bucketLabel(b.start, bucket)} — click to open EVERY work item of this period (all series showing)`}</title>}
              <text transform="rotate(-45)" textAnchor="end" fontSize="10" fontWeight="600" fill={hv === i && onPick ? ui.blue : ui.fg} textDecoration={hv === i && onPick ? 'underline' : 'none'} fontFamily={FONT}>{bucketLabel(b.start, bucket)}</text>
            </g>
          )))}
          <text x={(M.l + W - M.r) / 2} y={H - 4} textAnchor="middle" fontSize="10.5" fontWeight="700" fill={ui.faint} fontFamily={FONT}>{xLabel} — {bucketName.toUpperCase()}{cumulative ? ' (CUMULATIVE)' : ''}</text>
          <text transform={`translate(13 ${M.t + plotH / 2}) rotate(-90)`} textAnchor="middle" fontSize="11" fontWeight="700" fill={ui.fg} fontFamily={FONT}>{yLabel}</text>
          {barsOnAxis && <text transform={`translate(${W - 9} ${M.t + plotH / 2}) rotate(90)`} textAnchor="middle" fontSize="11" fontWeight="700" fill={ui.fg} fontFamily={FONT}>{activeBars.map((b) => b.label).join(' / ').toUpperCase()}{cumulative ? ' (CUMULATIVE)' : ''}</text>}
        </svg>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', marginTop: 4 }}>
          <span style={{ ...tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>X-axis</span>
          {BUCKETS.map((b) => (
            <button key={b.key} onClick={() => { setBucket(b.key); setHover(null); }} title={`Bucket the due dates ${b.label.toLowerCase()}`} style={pill(bucket === b.key)}>{b.label}</button>
          ))}
          <span style={{ width: 1, height: 18, background: ui.border, margin: '0 4px' }} />
          <span style={{ ...tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Y-axis</span>
          <button onClick={() => setYScale('lin')} title="Linear scale — equal steps" style={pill(!isLog)}>Linear</button>
          <button onClick={() => setYScale('log')} title="Log scale — each gridline is ×10; lifts the small series off the floor when one spike dominates (the columns keep their own linear axis)" style={pill(isLog)}>Log</button>
        </div>
        {/* legend: colored dot · series | total — click to hide a series */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', marginTop: 8, borderTop: `1px solid ${ui.border}`, paddingTop: 10 }}>
          {legendRows.map((r) => legendItem(r, <span style={{ width: 13, height: 13, borderRadius: 13, background: r.color, display: 'inline-block' }} />))}
          {controlled && allRows.filter((r) => !isOn(r.key) && trendOf(r.key)).map((r) => (
            <span key={`tr-${r.key}`} title={`${r.label} is unticked — only its trend line is drawn`}
              style={{ ...tiny, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 8, border: `1px dashed ${ui.border}`, background: ui.card }}>
              <span style={{ width: 16, height: 0, borderTop: `3px solid ${r.color || r.posColor}`, display: 'inline-block' }} />
              <span style={{ fontWeight: 700, color: ui.fg }}>{r.label} trend</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: ui.fg, fontVariantNumeric: 'tabular-nums' }}>{trendOf(r.key).reliability == null ? 'n/a' : `${(trendOf(r.key).reliability * 100).toFixed(1)}%`}</span>
            </span>
          ))}
          {barRows.map((r) => legendItem(r, (
            <span style={{ display: 'inline-flex', gap: 2, opacity: Math.min(1, (r.opacity ?? 0.32) + 0.35) }}>
              <span style={{ width: 6, height: 13, borderRadius: 2, background: r.posColor, display: 'inline-block' }} />
              <span style={{ width: 6, height: 13, borderRadius: 2, background: r.negColor, display: 'inline-block' }} />
            </span>
          )))}
          {legendLimit > 0 && rows.length > legendLimit && (
            <button onClick={() => setLegendAll((a) => !a)} style={{ ...tiny, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, fontFamily: FONT }}>
              {legendAll ? '▴ Show fewer' : `▸ Show more… (${rows.length - legendLimit})`}
            </button>
          )}
          {hv != null && <span style={{ ...tiny, fontWeight: 700 }}>{bucketLabel(buckets[hv].start, bucket)}</span>}
        </div>
      </>))}
    </div>
  );
}
